<template>
  <div class="usage-analytics-panel">
    <div class="panel">
      <div class="panel-title">
        {{ $t('hit-admin.subscriptions.usageAnalytics.forecastTitle') }}
      </div>
      <div class="panel-subtitle">
        {{ $t('hit-admin.subscriptions.usageAnalytics.forecastSubtitle') }}
      </div>

      <div class="panel-grid">
        <hit-select
          class="min-w-[32rem] w-full"
          :label="$t('hit-admin.subscriptions.usageAnalytics.company')"
          :options="companyOptions"
          :value="selectedCompanyId"
          item-id-property="id"
          :label-fields="['label']"
          filterable
          clearable
          @change="onCompanyChange"
        />
      </div>

      <div class="toolbar">
        <hit-button
          :label="$t('hit-admin.subscriptions.usageAnalytics.refresh')"
          color="button"
          prefix-icon="refresh"
          :disabled="loading"
          @click="loadData"
        />
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <div v-if="loading" class="loading-state">
        {{ $t('hit-admin.subscriptions.usageAnalytics.loading') }}
      </div>

      <template v-else-if="forecast">
        <div class="forecast-banner">
          <div>
            <div class="banner-label">
              {{
                $t('hit-admin.subscriptions.usageAnalytics.currentMonthAmount')
              }}
            </div>
            <div class="banner-value">
              {{ formatCurrency(forecast.current_amount) }}
            </div>
            <div class="banner-sub">
              {{ forecast.period_label }}
              <span v-if="forecast.computed_at">
                —
                {{
                  $t('hit-admin.subscriptions.usageAnalytics.lastComputedAt')
                }}:
                {{ formatDateTime(forecast.computed_at) }}
              </span>
            </div>
          </div>
          <div class="banner-meta">
            <div>
              {{ $t('hit-admin.subscriptions.usageAnalytics.previousAmount') }}
              ({{ forecast.previous_period_label }}):
              <strong>{{ formatCurrency(forecast.previous_amount) }}</strong>
            </div>
            <div>
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiModules') }}:
              <strong>{{ forecast.distinct_modules_count }}</strong>
            </div>
          </div>
        </div>

        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-label">
              {{
                $t('hit-admin.subscriptions.usageAnalytics.kpiCurrentVsPrev')
              }}
            </div>
            <div class="kpi-value">
              {{ formatPercent(forecast.variation_vs_previous_pct) }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiBilledUsers') }}
            </div>
            <div class="kpi-value">{{ forecast.billed_users_count }}</div>
            <div class="kpi-sub">
              {{ $t('hit-admin.subscriptions.usageAnalytics.previousUsers') }}:
              {{ forecast.previous_billed_users_count }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiArpu') }}
            </div>
            <div class="kpi-value">{{ formatCurrency(forecast.arpu) }}</div>
            <div class="kpi-sub">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiCompanies') }}:
              {{ forecast.billed_companies_count }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiModules') }}
            </div>
            <div class="kpi-value">{{ forecast.distinct_modules_count }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiCheckins') }}
            </div>
            <div class="kpi-value">
              {{ forecast.nsso_checkin_quantity || 0 }}
            </div>
            <div class="kpi-sub">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiSms') }}:
              {{ forecast.nsso_sms_quantity || 0 }}
            </div>
            <div class="kpi-sub">
              {{
                $t('hit-admin.subscriptions.usageAnalytics.previousCheckins')
              }}:
              {{ forecast.previous_nsso_checkin_quantity || 0 }}
              ({{ $t('hit-admin.subscriptions.usageAnalytics.kpiSms') }}:
              {{ forecast.previous_nsso_sms_quantity || 0 }})
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-title">
            {{
              $t('hit-admin.subscriptions.usageAnalytics.currentByTypeTitle')
            }}
          </div>
          <div class="bars">
            <div
              v-for="row in currentTypeRows"
              :key="`current-${row.key}`"
              class="bar-row"
            >
              <div class="bar-label">{{ row.label }}</div>
              <div class="bar-track">
                <div
                  class="bar-fill current"
                  :style="{width: `${row.widthPct}%`}"
                />
              </div>
              <div class="bar-value">{{ formatCurrency(row.amount) }}</div>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-title">
            {{ $t('hit-admin.subscriptions.usageAnalytics.topModulesTitle') }}
          </div>
          <div v-if="forecast.top_modules.length === 0" class="chart-empty">
            {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
          </div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>
                  {{
                    $t('hit-admin.subscriptions.usageAnalytics.columns.module')
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t(
                      'hit-admin.subscriptions.usageAnalytics.columns.quantity',
                    )
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t('hit-admin.subscriptions.usageAnalytics.columns.amount')
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in forecast.top_modules" :key="row.module_id">
                <td>{{ formatModuleLabel(row.designation_key) }}</td>
                <td class="col-num">{{ row.quantity }}</td>
                <td class="col-num">{{ formatCurrency(row.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi, COMPANY_OPTIONS_MAX_LIMIT} from '@admin/services/adminApi';

export default defineComponent({
  name: 'CurrentMonthForecastPanel',
  components: {
    HitButton,
    HitSelect,
  },
  data() {
    return {
      companyOptions: [],
      selectedCompanyId: null,
      loading: false,
      errorMessage: '',
      forecast: null,
    };
  },
  computed: {
    currentTypeRows() {
      return this.buildTypeRows(this.forecast?.amount_by_type || {});
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
    await this.loadData();
  },
  methods: {
    buildTypeRows(amountByType) {
      const items = [
        {
          key: 'module',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typeModule'),
          amount: Number(amountByType.module || 0),
        },
        {
          key: 'peppol',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typePeppol'),
          amount: Number(amountByType.peppol || 0),
        },
        {
          key: 'company_service',
          label: this.$t(
            'hit-admin.subscriptions.usageAnalytics.typeCompanyService',
          ),
          amount: Number(amountByType.company_service || 0),
        },
      ];
      const max = Math.max(...items.map((item) => item.amount), 0);
      return items.map((item) => ({
        ...item,
        widthPct: max > 0 ? Math.round((item.amount / max) * 100) : 0,
      }));
    },
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({
          limit: COMPANY_OPTIONS_MAX_LIMIT,
        });
        this.companyOptions = (res.rows || []).map((c) => ({
          id: c.id,
          label: [c.designation, c.vat_no].filter(Boolean).join(' — ') || c.id,
        }));
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedCompanyId = value || null;
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      this.errorMessage = '';
      try {
        this.forecast = await adminApi.getUsageForecast({
          company_id: this.selectedCompanyId,
        });
      } catch (e) {
        this.forecast = null;
        this.errorMessage =
          e?.message ||
          this.$t('hit-admin.subscriptions.usageAnalytics.loadError');
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('fr-BE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    formatPercent(value) {
      if (value === null || value === undefined) return '—';
      const sign = value > 0 ? '+' : '';
      return `${sign}${Number(value).toFixed(1)} %`;
    },
    formatDateTime(value) {
      if (!value) return '—';
      try {
        return new Intl.DateTimeFormat('fr-BE', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(value));
      } catch {
        return value;
      }
    },
    formatModuleLabel(key) {
      if (!key) return '—';
      return key.replace(/^module\./, '').replace(/_/g, ' ');
    },
  },
});
</script>

<style lang="scss" scoped>
.usage-analytics-panel {
  height: 100%;
  min-height: 0;
}

.panel {
  display: grid;
  gap: 0.75rem;
  padding: 0.75rem;
}

.panel-title {
  font-size: 1rem;
  font-weight: 650;
  color: rgba(15, 23, 42, 0.92);
}

.panel-subtitle {
  font-size: 0.82rem;
  color: rgba(71, 85, 105, 1);
}

.panel-grid,
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.alert-error,
.loading-state,
.chart-empty {
  font-size: 0.82rem;
  color: rgba(100, 116, 139, 1);
}

.alert-error {
  color: #b91c1c;
}

.forecast-banner {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  border: 1px solid rgba(44, 90, 160, 0.18);
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.08),
    rgba(255, 255, 255, 0.95)
  );
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
}

.banner-label {
  font-size: 0.78rem;
  color: rgba(71, 85, 105, 1);
}

.banner-value {
  margin-top: 0.2rem;
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e3a5f;
}

.banner-sub,
.banner-meta {
  font-size: 0.78rem;
  color: rgba(71, 85, 105, 1);
}

.banner-meta {
  display: grid;
  gap: 0.25rem;
  align-content: center;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
}

.kpi-card,
.chart-card {
  border: 1px solid var(--ho-border, rgba(15, 23, 42, 0.08));
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.7rem 0.85rem;
}

.kpi-label {
  font-size: 0.75rem;
  color: rgba(100, 116, 139, 1);
}

.kpi-value {
  margin-top: 0.3rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
}

.kpi-sub {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: rgba(71, 85, 105, 1);
}

.chart-title {
  font-size: 0.82rem;
  font-weight: 650;
  color: rgba(15, 23, 42, 0.82);
  margin-bottom: 0.45rem;
}

.bars {
  display: grid;
  gap: 0.35rem;
}

.bar-row {
  display: grid;
  grid-template-columns: 7rem minmax(0, 1fr) 6rem;
  align-items: center;
  gap: 0.5rem;
}

.bar-label {
  font-size: 0.75rem;
  color: rgba(51, 65, 85, 1);
}

.bar-track {
  height: 0.65rem;
  background: rgba(226, 232, 240, 0.9);
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
}

.bar-fill.current {
  background: #2c5aa0;
}

.bar-value {
  font-size: 0.75rem;
  text-align: right;
  color: rgba(15, 23, 42, 0.88);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.data-table th,
.data-table td {
  padding: 0.45rem 0.35rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  text-align: left;
}

.col-num {
  text-align: right;
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
